import React, { useState, useRef, useEffect } from 'react';
import './DateSelector.scss';
import { t } from '../../i18n/i18n';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const icon = (name) => ({ '--icon': `url(/icons/${name}.svg)` });

const parseDate = (val) => {
  if (!val) return null;
  if (val instanceof Date) return isNaN(val.getTime()) ? null : val;
  if (typeof val === 'string') {
    const parts = val.split('-');
    if (parts.length === 3) {
      return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    }
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

const toISODate = (date) => {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const isSameDay = (d1, d2) => {
  if (!d1 || !d2) return false;
  return (
    d1.getDate() === d2.getDate() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getFullYear() === d2.getFullYear()
  );
};

export default function DateSelector({ value, onChange, label }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const isRangeMode = Boolean(
    value &&
      typeof value === 'object' &&
      ('startDate' in value || 'start_date' in value)
  );

  const parsedStart = isRangeMode
    ? parseDate(value?.startDate || value?.start_date)
    : value instanceof Date
    ? value
    : parseDate(value);

  const parsedEnd = isRangeMode
    ? parseDate(value?.endDate || value?.end_date)
    : parsedStart;

  const [internalDate, setInternalDate] = useState(
    () => parsedStart || new Date(2026, 8, 1)
  );
  const [viewDate, setViewDate] = useState(
    () => parsedStart || new Date(2026, 8, 1)
  );
  const [pendingStart, setPendingStart] = useState(null);
  const [hasSelected, setHasSelected] = useState(false);

  const activeStart = pendingStart || parsedStart || internalDate;
  const activeEnd = pendingStart ? null : parsedEnd || internalDate;

  // Sync viewDate when value changes externally
  useEffect(() => {
    if (parsedStart) {
      setViewDate(new Date(parsedStart.getFullYear(), parsedStart.getMonth(), 1));
    }
  }, [value]);

  // Handle outside clicks to close the dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setPendingStart(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = () => {
    if (!isOpen) {
      if (activeStart) {
        setViewDate(new Date(activeStart.getFullYear(), activeStart.getMonth(), 1));
      }
      setPendingStart(null);
    }
    setIsOpen(!isOpen);
  };

  const handlePrevMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
  };

  const handleSelectDay = (dayObj) => {
    const clickedDate = new Date(dayObj.year, dayObj.month, dayObj.day);
    setHasSelected(true);

    if (isRangeMode) {
      if (!pendingStart) {
        setPendingStart(clickedDate);
      } else {
        const start = clickedDate < pendingStart ? clickedDate : pendingStart;
        const end = clickedDate < pendingStart ? pendingStart : clickedDate;
        setPendingStart(null);
        if (onChange) {
          onChange({
            startDate: toISODate(start),
            endDate: toISODate(end),
          });
        }
        setIsOpen(false);
      }
    } else {
      if (onChange) {
        onChange(clickedDate);
      } else {
        setInternalDate(clickedDate);
      }
      setIsOpen(false);
    }
  };

  const handleResetToCurrentMonth = () => {
    setPendingStart(null);
    setHasSelected(false);
    const baseYear = viewDate.getFullYear();
    const baseMonth = viewDate.getMonth();
    const start = new Date(baseYear, baseMonth, 1);
    const end = new Date(baseYear, baseMonth + 1, 1);

    if (isRangeMode) {
      if (onChange) {
        onChange({
          startDate: toISODate(start),
          endDate: toISODate(end),
        });
      }
    } else {
      if (onChange) {
        onChange(start);
      } else {
        setInternalDate(start);
      }
    }
    setIsOpen(false);
  };

  // Generate 42 days grid
  const daysGrid = [];
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const firstDayIndex = new Date(year, month, 1).getDay();
  const totalDays = new Date(year, month + 1, 0).getDate();
  const prevMonthTotalDays = new Date(year, month, 0).getDate();

  // Prev month padding days
  for (let i = firstDayIndex - 1; i >= 0; i--) {
    daysGrid.push({
      day: prevMonthTotalDays - i,
      month: month - 1,
      year: month === 0 ? year - 1 : year,
      isCurrentMonth: false,
    });
  }

  // Current month days
  for (let i = 1; i <= totalDays; i++) {
    daysGrid.push({
      day: i,
      month: month,
      year: year,
      isCurrentMonth: true,
    });
  }

  // Next month padding days
  const remaining = 42 - daysGrid.length;
  for (let i = 1; i <= remaining; i++) {
    daysGrid.push({
      day: i,
      month: month + 1,
      year: month === 11 ? year + 1 : year,
      isCurrentMonth: false,
    });
  }

  // Format the trigger button text
  const getTriggerLabel = () => {
    if (label) return t(label);
    if (isRangeMode && activeStart && activeEnd) {
      const startDay = activeStart.getDate();
      const startMonth = t(MONTH_NAMES[activeStart.getMonth()].substring(0, 3));
      const startYear = activeStart.getFullYear();

      const endDay = activeEnd.getDate();
      const endMonth = t(MONTH_NAMES[activeEnd.getMonth()].substring(0, 3));
      const endYear = activeEnd.getFullYear();

      if (isSameDay(activeStart, activeEnd)) {
        return `${startDay} ${startMonth} ${startYear}`;
      }
      if (startYear === endYear) {
        return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${startYear}`;
      }
      return `${startDay} ${startMonth} ${startYear} - ${endDay} ${endMonth} ${endYear}`;
    }

    if (!hasSelected && !value) {
      return t('This Month');
    }

    const targetDate = activeStart || new Date(2026, 8, 1);
    const day = targetDate.getDate();
    const monthStr = t(MONTH_NAMES[targetDate.getMonth()].substring(0, 3));
    const y = targetDate.getFullYear();
    return `${day} ${monthStr} ${y}`;
  };

  return (
    <div
      className={`date-selector ${isOpen ? 'is-open ac-menu-open' : ''}`}
      ref={containerRef}
    >
      <button className="date-selector__trigger" onClick={handleToggle} type="button">
        <span className="icon date-selector__icon-calendar" style={icon('metrics-calendar')} />
        <span className="date-selector__label">{getTriggerLabel()}</span>
        <span className="icon date-selector__icon-arrow" style={icon('chevron-down')} />
      </button>

      {isOpen && (
        <div className="date-selector__dropdown">
          <div className="date-selector__header">
            <button className="date-selector__nav-btn" onClick={handlePrevMonth} type="button">
              <span className="icon" style={{ ...icon('chevron-down'), transform: 'rotate(90deg)' }} />
            </button>
            <span className="date-selector__month-year">
              {t(MONTH_NAMES[month])} {year}
            </span>
            <button className="date-selector__nav-btn" onClick={handleNextMonth} type="button">
              <span className="icon" style={{ ...icon('chevron-down'), transform: 'rotate(-90deg)' }} />
            </button>
          </div>

          <div className="date-selector__weekdays">
            {WEEKDAYS.map((d) => (
              <span className="date-selector__weekday" key={d}>
                {t(d)}
              </span>
            ))}
          </div>

          <div className="date-selector__days-grid">
            {daysGrid.map((dayObj, index) => {
              const dateVal = new Date(dayObj.year, dayObj.month, dayObj.day);
              const isStart = activeStart && isSameDay(dateVal, activeStart);
              const isEnd = activeEnd && isSameDay(dateVal, activeEnd);
              const isInRange =
                activeStart &&
                activeEnd &&
                dateVal > activeStart &&
                dateVal < activeEnd;
              const isSelected = isStart || isEnd;
              const isToday = isSameDay(dateVal, new Date(2026, 8, 1));

              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleSelectDay(dayObj)}
                  className={`date-selector__day ${
                    dayObj.isCurrentMonth ? 'is-current-month' : 'is-other-month'
                  } ${isSelected ? 'is-selected' : ''} ${isStart ? 'is-range-start' : ''} ${
                    isEnd ? 'is-range-end' : ''
                  } ${isInRange ? 'is-in-range' : ''} ${isToday ? 'is-today' : ''}`}
                >
                  {dayObj.day}
                </button>
              );
            })}
          </div>

          <div className="date-selector__footer">
            <button
              className="date-selector__reset-btn"
              onClick={handleResetToCurrentMonth}
              type="button"
            >
              {t('This Month')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
